import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import RncLookupField, { NameFromDgiiField } from "@/components/RncLookupField";
import { Plus, Pencil, Trash2, X, Search } from "lucide-react";
import FieldHint from "@/components/FieldHint";
import { useConfirm } from "@/components/ConfirmDialog";
import { CustomerFiscalAction, FiscalClassBadge } from "@/components/fiscal/CustomerFiscalAction";


function MultiTagsInput({ value = [], onChange, options = [], placeholder = "Añadir…", testid }) {
  const [text, setText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const add = (raw) => {
    const v = (raw || "").trim();
    if (!v) return;
    if (value.map((s) => s.toLowerCase()).includes(v.toLowerCase())) {
      setText("");
      return;
    }
    onChange([...value, v]);
    setText("");
    setSuggestions([]);
  };
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(text);
    } else if (e.key === "Backspace" && !text && value.length) {
      remove(value.length - 1);
    }
  };
  const onChangeText = (v) => {
    setText(v);
    if (!v) { setSuggestions([]); return; }
    const s = v.toLowerCase();
    setSuggestions(
      options.filter((o) => o.toLowerCase().includes(s) && !value.map((x) => x.toLowerCase()).includes(o.toLowerCase())).slice(0, 6)
    );
  };
  return (
    <div className="mt-1 relative">
      <div className="min-h-9 border border-input rounded-md px-2 py-1.5 flex flex-wrap items-center gap-1 bg-background" data-testid={testid}>
        {value.map((tag, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-primary/10 text-primary px-2 py-0.5 rounded text-xs font-medium">
            {tag}
            <button
              type="button"
              onClick={() => remove(i)}
              className="hover:text-destructive"
              aria-label={`Quitar ${tag}`}
              data-testid={`${testid}-remove-${i}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={text}
          onChange={(e) => onChangeText(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => setTimeout(() => setSuggestions([]), 150)}
          placeholder={value.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[100px] bg-transparent outline-none text-sm"
          data-testid={`${testid}-input`}
        />
      </div>
      {suggestions.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-popover border border-border rounded shadow-md max-h-40 overflow-y-auto" data-testid={`${testid}-suggestions`}>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); add(s); }}
              className="block w-full text-left px-3 py-1.5 hover:bg-accent text-sm"
              data-testid={`${testid}-suggestion-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function genericCRUD({ title, subtitle, endpoint, columns, formFields, defaults, testidPrefix, extraLoad, searchable = false, searchPlaceholder = "Buscar…", rowActions }) {
  return function Page() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(defaults);
    const [editingId, setEditingId] = useState(null);
    const [extra, setExtra] = useState({});   // p.ej. { categories: [...] } inyectado por extraLoad
    const [query, setQuery] = useState("");
    const confirm = useConfirm();

    const load = async (q = "") => {
      const params = searchable && q.trim() ? { q: q.trim() } : undefined;
      const { data } = await api.get(endpoint, { params });
      setItems(data);
      if (extraLoad) {
        try { setExtra(await extraLoad()); } catch { /* silencioso */ }
      }
    };
    useEffect(() => { load(); }, []);

    // Debounce server-side search
    useEffect(() => {
      if (!searchable) return;
      const t = setTimeout(() => { load(query); }, 300);
      return () => clearTimeout(t);
    }, [query]);

    const openNew = () => { setForm(defaults); setEditingId(null); setOpen(true); };
    const openEdit = (it) => { setForm({ ...it }); setEditingId(it.id); setOpen(true); };

    const onSubmit = async (e) => {
      e.preventDefault();
      // Coerción numérica para campos numéricos (evita "5" en vez de 5)
      const payload = { ...form };
      formFields.forEach((f) => {
        if (f.type === "number" && payload[f.key] !== undefined && payload[f.key] !== "") {
          const n = Number(payload[f.key]);
          if (!Number.isNaN(n)) payload[f.key] = n;
        }
      });
      try {
        if (editingId) await api.put(`${endpoint}/${editingId}`, payload);
        else await api.post(endpoint, payload);
        toast.success("Guardado"); setOpen(false); load(query);
      } catch (err) { toast.error(formatApiError(err)); }
    };
    const onDelete = async (it) => {
      const ok = await confirm({
        title: `¿Eliminar ${it.name || "el registro"}?`,
        description: "Esta acción no se puede deshacer.",
        confirmLabel: "Eliminar",
        destructive: true,
      });
      if (!ok) return;
      try { await api.delete(`${endpoint}/${it.id}`); load(query); }
      catch (err) { toast.error(formatApiError(err)); }
    };

    return (
      <PageContainer>
        <PageHeader
          title={title}
          subtitle={subtitle}
          testid={`${testidPrefix}-header`}
          actions={
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={openNew} className="bg-primary font-bold" data-testid={`${testidPrefix}-new-btn`}>
                  <Plus className="w-4 h-4 mr-1.5" /> Nuevo
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{editingId ? "Editar" : "Nuevo"}</DialogTitle></DialogHeader>
                <form onSubmit={onSubmit} className="grid grid-cols-2 gap-4">
                  {formFields.map((f) => (
                    <div key={f.key} className={f.full ? "col-span-2" : ""}>
                      {f.type === "switch" ? (
                        <>
                          <FieldHint label={f.label} hint={f.hint} />
                          <div className="flex items-center gap-3 mt-2">
                            <Switch checked={!!form[f.key]} onCheckedChange={(v) => setForm({ ...form, [f.key]: v })} />
                            <span className="text-sm text-muted-foreground">{form[f.key] ? "Sí" : "No"}</span>
                          </div>
                        </>
                      ) : f.type === "multitags" ? (
                        <>
                          <FieldHint label={f.label} hint={f.hint} />
                          <MultiTagsInput
                            value={Array.isArray(form[f.key]) ? form[f.key] : []}
                            onChange={(vals) => setForm({ ...form, [f.key]: vals })}
                            options={f.optionsSource ? (extra[f.optionsSource] || []) : []}
                            placeholder={f.placeholder || "Añadir…"}
                            testid={`${testidPrefix}-${f.key}`}
                          />
                        </>
                      ) : f.type === "select" ? (
                        <>
                          <FieldHint label={f.label} hint={f.hint} required={f.required} />
                          <select
                            value={form[f.key] ?? f.options?.[0]?.value ?? ""}
                            onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                            className="mt-1 w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                            data-testid={`${testidPrefix}-${f.key}`}
                          >
                            {(f.options || []).map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </>
                      ) : f.type === "dgii_name" ? (
                        <>
                          <FieldHint label={f.label} hint={f.hint} required={f.required} />
                          <NameFromDgiiField
                            value={form[f.key] || ""}
                            onChange={(v) => setForm({ ...form, [f.key]: v })}
                            onPick={(info) => {
                              setForm((prev) => ({
                                ...prev,
                                [f.key]: info.razon_social,
                                rnc_cedula: info.rnc,
                              }));
                            }}
                            testid={`${testidPrefix}-${f.key}`}
                          />
                        </>
                      ) : f.type === "rnc_lookup" ? (
                        <>
                          <FieldHint label={f.label} hint={f.hint} required={f.required} />
                          <RncLookupField
                            value={form[f.key] || ""}
                            onChange={(v) => setForm({ ...form, [f.key]: v })}
                            onResolve={(info) => {
                              // Auto-rellena el nombre si el campo está vacío
                              if (info && !form.name) {
                                setForm((f) => ({ ...f, name: info.razon_social || info.nombre_comercial || f.name }));
                              }
                            }}
                            testid={`${testidPrefix}-${f.key}`}
                          />
                        </>
                      ) : (
                        <>
                          <FieldHint label={f.label} hint={f.hint} required={f.required} />
                          <Input
                            type={f.type || "text"}
                            value={form[f.key] || ""}
                            onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                            required={f.required}
                            className="mt-1"
                            data-testid={`${testidPrefix}-${f.key}`}
                          />
                        </>
                      )}
                    </div>
                  ))}
                  <DialogFooter className="col-span-2">
                    <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                    <Button type="submit" className="bg-primary font-bold">Guardar</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          }
        />
        <div className="bg-card border border-border rounded-md overflow-x-auto">
          {searchable && (
            <div className="p-3 border-b border-border flex items-center gap-2">
              <Search className="w-4 h-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-8 max-w-md"
                data-testid={`${testidPrefix}-search-input`}
              />
              {query && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setQuery("")}
                  data-testid={`${testidPrefix}-search-clear`}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
              <span className="text-xs text-muted-foreground ml-auto" data-testid={`${testidPrefix}-count`}>
                {items.length} resultado{items.length === 1 ? "" : "s"}
              </span>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="overline border-b border-border">
              <tr>
                {columns.map((c) => <th key={c.key} className={`p-3 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>)}
                <th className="text-right p-3 w-24">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-border/60 hover:bg-accent">
                  {columns.map((c) => (
                    <td key={c.key} className={`p-3 ${c.align === "right" ? "text-right data-cell" : ""} ${c.mono ? "data-cell" : ""}`}>
                      {c.render ? c.render(it) : (it[c.key] || "—")}
                    </td>
                  ))}
                  <td className="p-3 text-right whitespace-nowrap">
                    {rowActions && rowActions(it, () => load(query))}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(it)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => onDelete(it)}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={columns.length + 1} className="p-8 text-center text-muted-foreground">Sin registros</td></tr>}
            </tbody>
          </table>
        </div>
      </PageContainer>
    );
  };
}

export const Categories = genericCRUD({
  title: "Categorías",
  subtitle: "Clasifica tu inventario por familia de productos.",
  endpoint: "/categories",
  testidPrefix: "categories",
  defaults: { name: "", active: true },
  columns: [
    { key: "name", label: "Nombre" },
    { key: "active", label: "Estado", render: (it) => it.active ? "Activa" : "Inactiva" },
  ],
  formFields: [
    { key: "name", label: "Nombre", required: true, full: true },
    { key: "active", label: "Activa", type: "switch" },
  ],
});

export function Subcategories() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [filterCat, setFilterCat] = useState("all");
  const [open, setOpen] = useState(false);
  const empty = { name: "", category_id: null, active: true };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const confirm = useConfirm();

  const load = async () => {
    const [s, c] = await Promise.all([api.get("/subcategories"), api.get("/categories")]);
    setItems(s.data);
    setCats(c.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (it) => { setForm({ ...it }); setEditingId(it.id); setOpen(true); };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/subcategories/${editingId}`, form);
        toast.success("Sub-categoría actualizada");
      } else {
        await api.post("/subcategories", form);
        toast.success("Sub-categoría creada");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDelete = async (it) => {
    const ok = await confirm({
      title: `¿Eliminar sub-categoría "${it.name}"?`,
      description: "Esta acción no se puede deshacer.",
      confirmLabel: "Eliminar",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/subcategories/${it.id}`);
    toast.success("Eliminada");
    load();
  };

  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";
  const visible = filterCat === "all" ? items : items.filter((s) => s.category_id === filterCat);

  return (
    <PageContainer>
      <PageHeader
        title="Sub-categorías"
        subtitle="Refina la clasificación: cada sub-categoría pertenece a una categoría principal. Ej: 'Luminarias' bajo 'Eléctricos'."
        testid="subcategories-header"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="bg-primary hover:bg-primary/90 font-bold" data-testid="new-subcategory-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Nueva sub-categoría
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingId ? "Editar sub-categoría" : "Nueva sub-categoría"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <Label>Categoría padre</Label>
                  <Select value={form.category_id || ""} onValueChange={(v) => setForm({ ...form, category_id: v || null })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona una categoría" /></SelectTrigger>
                    <SelectContent>
                      {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nombre</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" data-testid="subcategory-name" />
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} id="sub-active" />
                  <Label htmlFor="sub-active">Activa</Label>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-primary font-bold" data-testid="save-subcategory-btn">Guardar</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-card border border-border rounded-md">
        <div className="p-3 border-b border-border flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Filtrar por categoría:</Label>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="h-8 w-56" data-testid="subcategory-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="overline border-b border-border">
              <tr>
                <th className="text-left p-3">Categoría</th>
                <th className="text-left p-3">Sub-categoría</th>
                <th className="text-left p-3">Estado</th>
                <th className="text-right p-3 w-28">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => (
                <tr key={s.id} className="border-b border-border/60 hover:bg-accent">
                  <td className="p-3 text-xs text-muted-foreground">{catName(s.category_id)}</td>
                  <td className="p-3 font-semibold">{s.name}</td>
                  <td className="p-3 text-xs">{s.active ? "Activa" : "Inactiva"}</td>
                  <td className="p-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(s)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => onDelete(s)}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
              {visible.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">Sin sub-categorías</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </PageContainer>
  );
}

export const Warehouses = genericCRUD({
  title: "Almacenes",
  subtitle: "Gestiona múltiples ubicaciones físicas para tu inventario.",
  endpoint: "/warehouses",
  testidPrefix: "warehouses",
  defaults: { name: "", address: "", is_default: false, active: true },
  columns: [
    { key: "name", label: "Nombre" },
    { key: "address", label: "Dirección" },
    { key: "is_default", label: "Predeterminado", render: (it) => it.is_default ? "Sí" : "No" },
  ],
  formFields: [
    { key: "name", label: "Nombre", required: true, full: true },
    { key: "address", label: "Dirección", full: true },
    { key: "is_default", label: "Predeterminado", type: "switch" },
    { key: "active", label: "Activo", type: "switch" },
  ],
});

export const Customers = genericCRUD({
  title: "Clientes",
  subtitle: "Registra clientes con RNC o cédula para emitir facturas con crédito fiscal. Para clientes con saldos históricos del sistema anterior, usa el módulo CxC Migración.",
  endpoint: "/customers",
  testidPrefix: "customers",
  rowActions: (it, reload) => <CustomerFiscalAction customer={it} onDone={reload} />,
  searchable: true,
  searchPlaceholder: "Buscar por código, nombre, RNC/cédula, teléfono o email…",
  defaults: { name: "", commercial_name: "", rnc_cedula: "", phone: "", email: "", address: "", notes: "", active: true, credit_days: 30, credit_limit: 0, max_overdue_days: 60, customer_type: "regular", country: "" },
  columns: [
    { key: "code", label: "Código", mono: true,
      render: (it) => (
        <span className="text-primary font-bold" data-testid={`customer-code-${it.id}`}>
          {it.code || "—"}
        </span>
      ),
    },
    { key: "name", label: "Nombre",
      render: (it) => (
        <div className="flex items-center gap-2">
          <div className="flex flex-col">
            <span>{it.name || "—"}</span>
            {it.commercial_name && (
              <span className="text-xs text-muted-foreground italic">
                alias: {it.commercial_name}
              </span>
            )}
          </div>
          {it.is_default && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded bg-primary/15 text-primary font-bold uppercase"
              title="Cliente por defecto en POS"
              data-testid={`default-badge-${it.id}`}
            >
              Default POS
            </span>
          )}
          {(it.saldo_inicial_cxc || 0) > 0 && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-700 dark:text-orange-400 font-bold uppercase"
              title={`Saldo migrado inicial: RD$ ${Number(it.saldo_inicial_cxc).toLocaleString("es-DO", {minimumFractionDigits: 2})}`}
              data-testid={`migrated-badge-${it.id}`}
            >
              CxC Mig.
            </span>
          )}
        </div>
      ),
    },
    { key: "rnc_cedula", label: "RNC / Cédula", mono: true },
    { key: "customer_type", label: "Clasif. fiscal", render: (it) => <FiscalClassBadge customer={it} /> },
    { key: "phone", label: "Teléfono", mono: true },
    { key: "credit_limit", label: "Límite crédito", mono: true,
      render: (it) => (it.credit_limit || 0) > 0
        ? `RD$${Number(it.credit_limit).toLocaleString("es-DO", {minimumFractionDigits: 2})}`
        : <span className="text-muted-foreground">Sin límite</span>
    },
    { key: "credit_days", label: "Días cred.", mono: true },
    { key: "email", label: "Email" },
  ],
  formFields: [
    { key: "name", label: "Razón social (nombre legal)", required: true, full: true, type: "dgii_name", hint: "Nombre legal del cliente — se usa para facturación, e-CF y reportes DGII. Escribe 3+ letras para buscar en el padrón DGII." },
    { key: "commercial_name", label: "Nombre comercial / Alias", full: true, hint: "Nombre común por el que se conoce al cliente. Sólo para display interno; la facturación siempre usa la razón social." },
    { key: "rnc_cedula", label: "RNC / Cédula", type: "rnc_lookup", hint: "RNC (9 dígitos) si es empresa, o cédula (11 dígitos) si es persona física. Se valida contra el padrón DGII en tiempo real. Déjalo vacío para cliente genérico sin régimen tributario." },
    { key: "country", label: "País (ISO: USA, MEX…)", hint: "La clasificación fiscal (zona franca, ONG, gobierno, exportación…) y el documento de exención E44 se asignan con el botón del escudo en la lista de clientes." },
    { key: "foreign_id", label: "Identificación fiscal extranjera", hint: "Tax ID del cliente extranjero (E46)." },
    { key: "phone", label: "Teléfono", hint: "Para contacto y envío de notificaciones." },
    { key: "email", label: "Email", hint: "Si está presente y configuras Resend, las facturas pueden enviarse automáticamente." },
    { key: "credit_limit", label: "Monto máximo de crédito (RD$)", type: "number", hint: "Tope máximo de facturas a crédito pendientes. 0 = sin crédito (los siguientes campos se ignoran). Las nuevas ventas se rechazan si (pendiente + venta actual) > límite." },
    { key: "credit_days", label: "Días de crédito (vigente)", type: "number", hint: "Plazo dentro del cual la factura está VIGENTE. Obligatorio si otorgas crédito. Rango 1–90. Default: 30." },
    { key: "max_overdue_days", label: "Días máx. sin pagar (fuera de plazo)", type: "number", hint: "Total de días permitidos desde la emisión antes de bloquear al cliente. Debe ser ≥ 'Días de crédito' (la diferencia es el periodo de gracia). Rango 1–90. Default: 60." },
    { key: "address", label: "Dirección", full: true },
    { key: "notes", label: "Notas", full: true, hint: "Comentarios internos sobre el cliente (no aparece en facturas)." },
    { key: "active", label: "Activo", type: "switch" },
  ],
});

export const Suppliers = genericCRUD({
  title: "Suplidores",
  subtitle: "Tus proveedores de mercancía e insumos.",
  endpoint: "/suppliers",
  testidPrefix: "suppliers",
  searchable: true,
  searchPlaceholder: "Buscar por nombre, RNC, teléfono, email o tipo de artículo…",
  extraLoad: async () => {
    const { data } = await api.get("/categories");
    return { categories: (data || []).map((c) => c.name) };
  },
  defaults: { name: "", commercial_name: "", rnc_cedula: "", phone: "", email: "", address: "", notes: "", active: true, product_types: [], supplier_type: "regular", country: "" },
  columns: [
    { key: "name", label: "Nombre",
      render: (it) => (
        <div className="flex flex-col">
          <span>{it.name || "—"}</span>
          {it.commercial_name && (
            <span className="text-xs text-muted-foreground italic">
              alias: {it.commercial_name}
            </span>
          )}
        </div>
      ),
    },
    { key: "rnc_cedula", label: "RNC", mono: true },
    { key: "phone", label: "Teléfono", mono: true },
    { key: "email", label: "Email" },
    {
      key: "product_types",
      label: "Tipos de artículos",
      render: (it) => {
        const arr = Array.isArray(it.product_types) ? it.product_types : [];
        if (arr.length === 0) return <span className="text-muted-foreground">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {arr.slice(0, 3).map((t, i) => (
              <span key={i} className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary font-medium">{t}</span>
            ))}
            {arr.length > 3 && <span className="text-xs text-muted-foreground">+{arr.length - 3}</span>}
          </div>
        );
      },
    },
  ],
  formFields: [
    { key: "name", label: "Razón social (nombre legal)", required: true, full: true, type: "dgii_name", hint: "Nombre legal del proveedor — se usa para facturación, e-CF y reporte 606 DGII." },
    { key: "commercial_name", label: "Nombre comercial / Alias", full: true, hint: "Nombre común por el que se conoce al proveedor. Sólo para display interno; la facturación siempre usa la razón social." },
    { key: "rnc_cedula", label: "RNC / Cédula", type: "rnc_lookup", hint: "Empresa: RNC (9 dígitos). Persona física: cédula (11 dígitos), equivalente al RNC; obligatoria para emitirle E41. Se valida contra el padrón DGII (606)." },
    { key: "supplier_type", label: "Tipo de proveedor", type: "select",
      options: [
        { value: "regular",        label: "Formal – emite su comprobante (E31)" },
        { value: "informal",       label: "Persona física informal – tú emites E41" },
        { value: "foreign",        label: "Extranjero no residente – tú emites E47 (Gastos)" },
      ],
      hint: "Formal: te entrega su NCF/e-CF y tú solo lo registras. Informal: no puede darte comprobante; Amelia emite el E41 (Compras o Gastos) con su cédula. Extranjero: pagos de servicios al exterior con E47 desde Gastos." },
    { key: "country", label: "País (ISO: USA, CHN…)", hint: "Requerido para proveedores extranjeros (E47)." },
    { key: "foreign_id", label: "Identificación extranjera (Tax ID o pasaporte)", hint: "Tax ID / NIF de la empresa o pasaporte de la persona física extranjera no residente. Requerido para emitir E47 (no válido para E41)." },
    { key: "phone", label: "Teléfono" },
    { key: "email", label: "Email" },
    { key: "address", label: "Dirección", full: true },
    {
      key: "product_types",
      label: "Tipos de artículos que suple",
      type: "multitags",
      full: true,
      optionsSource: "categories",
      placeholder: "Cemento, Cabillas, Pintura…",
      hint: "Selecciona una o más categorías de artículos. Puedes escribir libremente o elegir de tu catálogo de categorías. Pulsa Enter o coma para agregar.",
    },
    { key: "notes", label: "Notas", full: true },
    { key: "active", label: "Activo", type: "switch" },
  ],
});
